import type { ComponentValueEntry, VariantMetadata } from "./types";

/**
 * @param value - Component variant value
 * @returns Whether the value should survive empty/falsy filtering
 */
export const isPresentComponentValue = (value: string): boolean => Boolean(value) && value !== "";

/**
 * Lists present (non-empty) values with their original indices.
 *
 * @param values - Component values in variant order
 * @returns Present entries
 */
export const listPresentComponentEntries = (values: string[]): ComponentValueEntry[] => {
  const entries: ComponentValueEntry[] = [];
  values.forEach((value, index) => {
    if (!isPresentComponentValue(value)) {
      return;
    }
    entries.push({ value, index });
  });
  return entries;
};

/**
 * Lists present values that are not deactivated, with original indices.
 *
 * @param values - Component values in variant order
 * @param deactivatedValues - Values marked deactivated for this component
 * @returns Active entries
 */
export const listActiveComponentEntries = (
  values: string[],
  deactivatedValues: ReadonlySet<string> = new Set()
): ComponentValueEntry[] => {
  return listPresentComponentEntries(values).filter(({ value }) => !deactivatedValues.has(value));
};

/**
 * @param sourceMetadata - Optional metadata aligned to the original values array
 * @param entries - Kept entries (present or active)
 * @returns Metadata for those entries, or undefined when `sourceMetadata` was omitted
 */
export const metadataForComponentEntries = (
  sourceMetadata: VariantMetadata[] | undefined,
  entries: ComponentValueEntry[]
): VariantMetadata[] | undefined => {
  if (!sourceMetadata) {
    return undefined;
  }
  return entries.map(({ index }) => sourceMetadata[index] ?? {});
};
